import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import type { Invoice } from '@repo/api-client/dtos';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { deleteJSON } from '../internal/http';

const OkSchema = z.object({ ok: z.boolean() }).passthrough();

export function useDeleteInvoice() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      if (!orgId) throw new Error('No active organization');
      await deleteJSON(http, `/orgs/${orgId}/invoices/${invoiceId}`, OkSchema);
      return invoiceId;
    },
    onSuccess: (invoiceId) => {
      if (!orgId) return;
      queryClient.removeQueries({ queryKey: queryKeys.invoices.detail(orgId, invoiceId) });
      // List cache is `InfiniteData<{ items, nextCursor? }>` since
      // the B1 (2026-05-09) migration to `useInfiniteQuery`.
      type InvoicesPage = { items: Invoice[]; nextCursor?: string };
      type InvoicesCache = { pages: InvoicesPage[]; pageParams: unknown[] };
      queryClient.setQueryData<InvoicesCache>(
        queryKeys.invoices.all(orgId),
        (cache) =>
          cache
            ? {
                ...cache,
                pages: cache.pages.map((page) => ({
                  ...page,
                  items: page.items.filter((inv) => inv.id !== invoiceId),
                })),
              }
            : cache,
      );
    },
  });

  return {
    deleteInvoice: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
